import * as React from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { WebPortalClient, CalculationMethod } from "webportalclient";
import { Form, useFormApi } from "informed";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { Checkbox } from "../../../forms/informedv2/Checkbox";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { ConvertToSelectListItemArray } from "./ParameterSelectList";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { AuditModal, AuditForm } from "../../../auditmodal/components/AuditModal";
import { ActionButtons } from "../../../misc/ActionButtons";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { StudyDlcoSetting, UnitType } from "../../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";

interface DlcoSettingsScreenFormState {
    disableOvereadWithoutHb: boolean;
    dlcoDisplayUnits: UnitType[];
    kcoDisplayUnits: UnitType[];
    dlcoTestTolerance: number;
    displayedParameters: ISelectListItem[];
    aggregateCalculationLimit: { Value: number };
    dlcoBestTestMethod: CalculationMethod;
    kcoBestTestMethod: CalculationMethod;
    vaBestTestMethod: CalculationMethod;
}

interface DlcoSettingsScreenFormProps {
    studyId: string;
    parameters: any[];
    dlcoUnitTypes: UnitType[];
    kcoUnitTypes: UnitType[];
    dlcoCalcMethods: CalculationMethod[];
    kcoCalcMethods: CalculationMethod[];
    vaCalcMethods: CalculationMethod[];
    aggregateCalculationLimits: { Value: number }[];
}

export const DlcoSettingsScreenForm = ({ studyId, parameters, dlcoCalcMethods, kcoCalcMethods, vaCalcMethods, aggregateCalculationLimits, dlcoUnitTypes, kcoUnitTypes }: DlcoSettingsScreenFormProps) => {
    const formApi = useFormApi<DlcoSettingsScreenFormState>();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const onPreAuditSubmit = () => {
        setAlertShown(false);
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        try {
            const result = await Promise.all([
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.AggregateCalculationLimitSetting", state.values.aggregateCalculationLimit.Value),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.BestTestCalculationMethodSetting", state.values.dlcoBestTestMethod.id),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.DisableDlcoOverreadingWithoutHBValue", state.values.disableOvereadWithoutHb),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.DisplayedDlcoParametersSetting", state.values.displayedParameters.map(d => d.Id)),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.DlcoDisplayUnitsSetting", state.values.dlcoDisplayUnits.map(d => d.Id)),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.DlcoTestToleranceSetting", state.values.dlcoTestTolerance),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.KcoBestTestCalculationMethodSetting", state.values.kcoBestTestMethod.id),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.KcoDisplayUnitsSetting", state.values.kcoDisplayUnits.map(k => k.Id)),
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Dlco.VABestTestCalculationMethodSetting", state.values.vaBestTestMethod.id),
            ]);
            
            if (result.every(r => r === true)) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    }

    const parametersSelectList: ISelectListItem[] = parameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    return (
        <>
        <Alert show={alertShown} category={AlertCategoryEnum.Success}>
            Saved successfully. Return to{" "}
            <Link to={`/studies`}>Studies list</Link>?
        </Alert>
        <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
            {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">DLco Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-4 col-sm-6">
                                    <label>Disable DLco Over-reading without HB</label>
                                    <p className="help-block">Determine whether to disable the Over-reading when the HB Value is not present.</p>
                                    <Checkbox id="disableOvereadWithoutHb" field="disableOvereadWithoutHb" label="" />
                                </div>
                                <div className="form-group col-md-4 col-sm-6">
                                    <label>DLco Display Units</label>
                                    <p className="help-block">The units to display the DLco parameter in</p>
                                    <MultiSelect
                                        id="dlcoDisplayUnits"
                                        field="dlcoDisplayUnits"
                                        options={dlcoUnitTypes}
                                        labelKey="Name"
                                        valueKey="Id"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-4 col-sm-6">
                                    <label>Kco Display Units</label>
                                    <p className="help-block">The units to display the Kco parameter in</p>
                                    <MultiSelect
                                        id="kcoDisplayUnits"
                                        field="kcoDisplayUnits"
                                        options={kcoUnitTypes}
                                        labelKey="Name"
                                        valueKey="Id"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-12 col-sm-6">
                                    <label>Displayed Parameters</label>
                                    <p className="help-block">The parameters that should be displayed on the DLco Over-read Screen</p>
                                    <MultiSelect
                                        id="displayedParameters"
                                        field="displayedParameters"
                                        options={parametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Best Test Calculation Settings</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-sm-6">
                                    <label>Aggregate Calculation Limit</label>
                                    <p className="help-block">The number of tests to select when calculating best values.</p>
                                    <MultiSelect
                                        id="aggregateCalculationLimit"
                                        field="aggregateCalculationLimit"
                                        options={aggregateCalculationLimits}
                                        labelKey="Value"
                                        valueKey="Value"
                                    />
                                </div>
                                <div className="form-group col-sm-6">
                                    <label>Dlco Test Tolerance</label>
                                    <p className="help-block">The tolerance used (in mL CO/min/mm Hg) to determine repeatable DLco tests.</p>
                                    <TextBoxWithStaticSuffix
                                        id="dlcoTestTolerance"
                                        field="dlcoTestTolerance"
                                        type="number"
                                        min="1"
                                        step="0.01"
                                        staticpart="mL CO/min/mm Hg"
                                    />
                                </div>
                                <div className="form-group col-sm-12">
                                    <label>DLco Best Test Calculation Method</label>
                                    <p className="help-block">The method used to calculate the best test for a DLco session. Note: The 'X' value refers to the Aggregate Calculation Limit value.</p>
                                    <MultiSelect
                                        id="dlcoBestTestMethod"
                                        field="dlcoBestTestMethod"
                                        options={dlcoCalcMethods}
                                        labelKey="description"
                                        valueKey="id"
                                    />
                                </div>
                                <div className="form-group col-sm-12">
                                    <label>Kco Best Test Calculation Method</label>
                                    <p className="help-block">The method used to calculate the best KCO test for a DLco session.</p>
                                    <MultiSelect
                                        id="kcoBestTestMethod"
                                        field="kcoBestTestMethod"
                                        options={kcoCalcMethods}
                                        labelKey="description"
                                        valueKey="id"
                                    />
                                </div>
                                <div className="form-group col-sm-12">
                                    <label>VA Best Test Calculation Method</label>
                                    <p className="help-block">The method used to calculate the best VA test for a DLco session. Note: The 'X' value refers to the Aggregate Calculation Limit value.</p>
                                    <MultiSelect
                                        id="vaBestTestMethod"
                                        field="vaBestTestMethod"
                                        options={vaCalcMethods}
                                        labelKey="description"
                                        valueKey="id"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
                {auditModalVisible ? (
                    <AuditModal
                        header="Approval"
                        isSubmitting={submitting}
                        onClose={() => setAuditModalVisible(false)}
                        onSubmit={(audit) => onSubmit(audit)}
                    />
                ) : null}
            </div>
        </>
    );
}

export const DlcoSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<DlcoSettingsScreenFormState>();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [parameters, setParameters] = React.useState([]);
    const [dlcoCalcMethods, setDlcoCalcMethods] = React.useState<CalculationMethod[]>();
    const [kcoCalcMethods, setKcoCalcMethods] = React.useState<CalculationMethod[]>();
    const [vaCalcMethods, setVaCalcMethods] = React.useState<CalculationMethod[]>();
    const [dlcoSettings, setDlcoSettings] = React.useState<StudyDlcoSetting>();

    const aggregateCalculationLimits = [1, 2, 3, 4, 5].map(x => ({ Value: x }));

    useCookieRedirect(id);

    React.useEffect(() => {
        const getSettings = async () => {
            const dlcoParametersResult = await legacyClient.getParametersBySessionType(SessionTypes.Dlco);
            setParameters(dlcoParametersResult);

            const dlcoCalcMethodsResult = await client.client.dlcoCalculationMethod();
            setDlcoCalcMethods(dlcoCalcMethodsResult.value);

            const kcoCalcMethodsResult = await client.client.kcoCalculationMethod();
            setKcoCalcMethods(kcoCalcMethodsResult.value);

            const vaCalcMethodsResult = await client.client.vaCalculationMethod();
            setVaCalcMethods(vaCalcMethodsResult.value);

            const settings = await legacyClient.getStudyDlcoSettings(id);
            setDlcoSettings(settings);

            setInitialValues({
                disableOvereadWithoutHb: settings.DisableDlcoOverreadWithoutHB,
                displayedParameters: ConvertToSelectListItemArray(settings.DisplayedParameters, dlcoParametersResult),
                dlcoDisplayUnits: settings.DlcoDisplayUnits.map(d => settings.DlcoUnits.find(ut => ut.Name === d)),
                kcoDisplayUnits: settings.KcoDisplayUnits.map(d => settings.KcoUnits.find(ut => ut.Name === d)),
                dlcoTestTolerance: settings.DlcoTestTolerance,
                aggregateCalculationLimit: aggregateCalculationLimits.find(a => a.Value === settings.AggregateCalculationLimit),
                dlcoBestTestMethod: dlcoCalcMethodsResult.value.find(d => d.id === settings.DlcoBestTestCalculationMethod),
                kcoBestTestMethod: kcoCalcMethodsResult.value.find(k => k.id === settings.KcoBestTestCalculationMethod),
                vaBestTestMethod: vaCalcMethodsResult.value.find(v => v.id === settings.VABestTestCalculationMethod)
            });
        }

        getSettings();
    }, [client.client, id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading DLco Settings..." />;
    }

    const validateFields = (values) => {
        return {
            displayedParameters: values.displayedParameters === null ? "At least one value must be selected" : undefined,
            dlcoDisplayUnits: values.dlcoDisplayUnits === null ? "At least one value must be selected" : undefined,
            kcoDisplayUnits: values.kcoDisplayUnits === null ? "At least one value must be selected" : undefined,
        }
    }

    return (
        <Form noValidate initialValues={initialValues} validateFields={validateFields}>
            <DlcoSettingsScreenForm studyId={id} parameters={parameters} dlcoUnitTypes={dlcoSettings.DlcoUnits} kcoUnitTypes={dlcoSettings.KcoUnits} dlcoCalcMethods={dlcoCalcMethods} kcoCalcMethods={kcoCalcMethods} vaCalcMethods={vaCalcMethods} aggregateCalculationLimits={aggregateCalculationLimits} />
        </Form>
    );
}
